/**
 * Global Vuetify Validation Rules
 * Compatible with Vuetify v-text-field, v-select, etc. rules prop
 *
 * Usage: <v-text-field :rules="$rules.email" />
 */
const specialCharacter = `', ", ;, --, *, #, |, %, _, \\,<, =, {, \`, $, ..`;
const errorMessage = `Text must not include any of the following characters: \n ${specialCharacter}`;
const patternInput =
  /^(?!.*(?:\.\.\/|\.\.|--|\/\*|\*\/|\|\||&&|%00|['"`;\\<>=$#*%_{}|!+[\]^~?]))[\p{L}\p{N}\p{S}\p{P}\s]*$/u;

export type ValidationRule = (value: any) => boolean | string;

// Special Character Validators
export function isContainSpecialCharacter(
  value: string | null | undefined
): boolean {
  if (value && value.length > 0) {
    const pattern = patternInput;

    if (!pattern.test(value)) return true;

    return false;
  }
  return false;
}

// Replace special characters in a string
export function replaceSpecialCharacters(
  value: string,
  replaceWith = ``
): string {
  if (value && value.length > 0) {
    return value.replace(
      /['"`;\\<>=$#*%_{}|!+[\]^~?]|(\.\.\/)|\.\.|--|\/\*|&&/g,
      replaceWith
    );
  }
  return value;
}

/**
 * Basic Validators
 */
const validators = {
  // Required
  required: (value: any): boolean | string => {
    if (value === null || value === undefined) return 'This field is required';
    if (typeof value === 'string' && !value.trim())
      return 'This field is required';
    if (Array.isArray(value) && value.length === 0)
      return 'This field is required';
    return true;
  },

  // Required with custom message
  requiredWithCustomMessage:
    (message: string = 'This field is required') =>
    (value: string): boolean | string => {
      if (value === null || value === undefined) return message;
      if (typeof value === 'string' && !value.trim()) return message;
      if (Array.isArray(value) && value.length === 0) return message;
      return true;
    },

  // Email
  email: (value: string): boolean | string => {
    if (!value) return true; // Allow empty if not required
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value) || 'Please enter a valid email address';
  },

  // Required email array (at least one email)
  emailArray: (value: string[]): boolean | string => {
    if (!value || value.length === 0) {
      return 'At least one email is required';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails: string[] = [];

    value.forEach((email) => {
      if (!emailRegex.test(email.trim())) {
        invalidEmails.push(email);
      }
    });

    if (invalidEmails.length > 0) {
      return `Invalid email(s): ${invalidEmails.join(', ')}`;
    }

    return true;
  },

  // Validate array of emails
  emailArrayOptional: (value: string[]): boolean | string => {
    if (!value || value.length === 0) return true; // Allow empty if not required

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails: string[] = [];

    value.forEach((email) => {
      if (!emailRegex.test(email.trim())) {
        invalidEmails.push(email);
      }
    });

    if (invalidEmails.length > 0) {
      return `Invalid email(s): ${invalidEmails.join(', ')}`;
    }

    return true;
  },

  // Minimum length
  minLength:
    (min: number) =>
    (value: string): boolean | string => {
      if (!value) return true;
      return value.length >= min || `Minimum ${min} characters required`;
    },

  // Maximum length
  maxLength:
    (max: number) =>
    (value: string): boolean | string => {
      if (!value) return true;
      return value.length <= max || `Maximum ${max} characters allowed`;
    },

  // Exact length
  exactLength:
    (length: number) =>
    (value: string): boolean | string => {
      if (!value) return true;
      return value.length === length || `Must be exactly ${length} characters`;
    },

  // Phone number
  phone: (value: string): boolean | string => {
    if (!value) return true;
    const phoneRegex = /^[\d\s\-+()]+$/;
    const digitsOnly = value.replace(/\D/g, '');
    if (!phoneRegex.test(value)) return 'Invalid phone number format';
    return digitsOnly.length >= 10 || 'Phone number must be at least 10 digits';
  },

  // Numeric
  numeric: (value: string): boolean | string => {
    if (!value) return true;
    return !isNaN(Number(value)) || 'Must be a number';
  },

  // Alphanumeric
  alphanumeric: (value: string): boolean | string => {
    if (!value) return true;
    return /^[a-zA-Z0-9]+$/.test(value) || 'Only letters and numbers allowed';
  },

  // Alpha only
  alpha: (value: string): boolean | string => {
    if (!value) return true;
    return /^[a-zA-Z\s]+$/.test(value) || 'Only letters allowed';
  },

  // Strong password
  strongPassword: (value: string): boolean | string => {
    if (!value) return true;
    const hasLower = /[a-z]/.test(value);
    const hasUpper = /[A-Z]/.test(value);
    const hasNumber = /\d/.test(value);
    const hasSpecial = /[@$!%*?&]/.test(value);
    const isLongEnough = value.length >= 8;

    if (!isLongEnough) return 'Password must be at least 8 characters';
    if (!hasLower) return 'Password must contain lowercase letter';
    if (!hasUpper) return 'Password must contain uppercase letter';
    if (!hasNumber) return 'Password must contain a number';
    if (!hasSpecial) return 'Password must contain special character (@$!%*?&)';

    return true;
  },

  // URL
  url: (value: string): boolean | string => {
    if (!value) return true;

    // Check for basic URL pattern first
    const urlPattern =
      /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
    if (!urlPattern.test(value)) {
      return 'Please enter a valid URL';
    }

    try {
      const url = new URL(
        value.startsWith('http') ? value : `https://${value}`
      );
      // Ensure it has a valid protocol and hostname
      if (!url.protocol.match(/^https?:$/) || !url.hostname.includes('.')) {
        return 'Please enter a valid URL';
      }
      return true;
    } catch {
      return 'Please enter a valid URL';
    }
  },

  // Match field
  match:
    (compareValue: string, fieldName: string = 'field') =>
    (value: string): boolean | string => {
      return value === compareValue || `Must match ${fieldName}`;
    },

  // Minimum value
  min:
    (min: number) =>
    (value: number): boolean | string => {
      if (!value && value !== 0) return true;
      return Number(value) >= min || `Minimum value is ${min}`;
    },

  // Maximum value
  max:
    (max: number) =>
    (value: number): boolean | string => {
      if (!value && value !== 0) return true;
      return Number(value) <= max || `Maximum value is ${max}`;
    },

  // Between values
  between:
    (min: number, max: number) =>
    (value: number): boolean | string => {
      if (!value && value !== 0) return true;
      const num = Number(value);
      return (
        (num >= min && num <= max) || `Value must be between ${min} and ${max}`
      );
    },

  // Date format
  date: (value: string): boolean | string => {
    if (!value) return true;
    return !isNaN(Date.parse(value)) || 'Please enter a valid date';
  },

  // Before date
  beforeDate:
    (compareDate: string) =>
    (value: string): boolean | string => {
      if (!value) return true;
      return (
        new Date(value) < new Date(compareDate) ||
        `Date must be before ${compareDate}`
      );
    },

  // After date
  afterDate:
    (compareDate: string) =>
    (value: string): boolean | string => {
      if (!value) return true;
      return (
        new Date(value) > new Date(compareDate) ||
        `Date must be after ${compareDate}`
      );
    },

  // Minimum age
  minAge:
    (minAge: number) =>
    (birthDate: string): boolean | string => {
      if (!birthDate) return true;
      const birth = new Date(birthDate);
      const today = new Date();
      const age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();

      let actualAge = age;
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birth.getDate())
      ) {
        actualAge = age - 1;
      }

      return actualAge >= minAge || `You must be at least ${minAge} years old`;
    },

  // Credit card
  creditCard: (value: string): boolean | string => {
    if (!value) return true;
    const cleanValue = value.replace(/\s/g, '');
    return (
      /^\d{13,19}$/.test(cleanValue) ||
      'Please enter a valid credit card number'
    );
  },

  // Regex pattern
  pattern:
    (pattern: RegExp, message: string = 'Invalid format') =>
    (value: string): boolean | string => {
      if (!value) return true;
      return pattern.test(value) || message;
    },

  // Free text array
  freeTextArray: (value: string[]): boolean | string => {
    if (!value || value.length === 0) {
      return 'At least one item is required';
    }

    const texts: string[] = [];

    value.forEach((email) => {
      if (!patternInput.test(email.trim())) {
        texts.push(email);
      }
    });

    if (texts.length > 0) {
      return `Invalid item(s): ${texts.join(', ')}`;
    }

    return true;
  },

  // Free text array (optional)
  freeTextArrayOptional: (value: string[]): boolean | string => {
    if (!value || value.length === 0) return true; // Allow empty if not required

    const texts: string[] = [];

    value.forEach((text) => {
      if (!patternInput.test(text.trim())) {
        texts.push(text);
      }
    });

    if (texts.length > 0) {
      return `Invalid item(s): ${texts.join(', ')}`;
    }

    return true;
  },

  // In array
  in:
    (allowedValues: any[], message?: string) =>
    (value: any): boolean | string => {
      if (!value) return true;
      return (
        allowedValues.includes(value) ||
        message ||
        `Value must be one of: ${allowedValues.join(', ')}`
      );
    },

  // Not in array
  notIn:
    (disallowedValues: any[], message?: string) =>
    (value: any): boolean | string => {
      if (!value) return true;
      return (
        !disallowedValues.includes(value) ||
        message ||
        'This value is not allowed'
      );
    },

  // File size (in MB)
  fileSize:
    (maxSizeMB: number) =>
    (file: File): boolean | string => {
      if (!file) return true;
      const sizeMB = file.size / 1024 / 1024;
      return (
        sizeMB <= maxSizeMB || `File size must be less than ${maxSizeMB}MB`
      );
    },

  // File type
  fileType:
    (allowedTypes: string[]) =>
    (file: File): boolean | string => {
      if (!file) return true;
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      return (
        (fileExtension && allowedTypes.includes(fileExtension)) ||
        `Allowed file types: ${allowedTypes.join(', ')}`
      );
    },

  /**
   * SQL Injection Prevention Validators
   */

  // Detect SQL injection patterns
  noSqlInjection: (value: string): boolean | string => {
    if (!value) return true;

    // Common SQL injection patterns
    const sqlInjectionPatterns = [
      /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|EXECUTE|UNION|JOIN)\b)/gi,
      // eslint-disable-next-line no-useless-escape
      /(--|\#|\/\*|\*\/)/g, // SQL comments
      // eslint-disable-next-line no-useless-escape
      /('|(\\')|(;)|(\-\-)|(\/\*))/g, // SQL special characters
      /(\bOR\b.*=.*)/gi, // OR 1=1 patterns
      /(\bAND\b.*=.*)/gi, // AND 1=1 patterns
      /(UNION.*SELECT)/gi, // Union-based injection
      /(SELECT.*FROM.*WHERE)/gi, // Basic SQL query
      /(\bEXEC\b|\bEXECUTE\b)/gi, // Execute commands
      /(xp_|sp_)/gi, // SQL Server extended/system procedures
      /(<script|javascript:|onerror=|onload=)/gi, // XSS attempts combined with SQL
      /(DROP\s+TABLE|DROP\s+DATABASE)/gi, // Dangerous commands
      /(SLEEP\(|BENCHMARK\(|WAITFOR)/gi, // Time-based blind injection
      /(LOAD_FILE|INTO\s+OUTFILE|INTO\s+DUMPFILE)/gi, // File operations
      /(CONCAT\(|GROUP_CONCAT\(|CHAR\()/gi, // String manipulation for injection
      /(\|\||&&)/g, // Logical operators
      /(0x[0-9A-F]+)/gi // Hexadecimal values
    ];

    for (const pattern of sqlInjectionPatterns) {
      if (pattern.test(value)) {
        return 'Invalid input detected. Please remove special characters or SQL keywords.';
      }
    }

    return true;
  },

  // Safe input with apostrophe allowed
  safeInput: (value: string): boolean | string => {
    if (!value) return true;

    // Allow: letters, numbers, spaces, apostrophe, and safe punctuation (. , - _ @ + () % /)
    // Including unicode characters for international names
    const safePattern = /^[a-zA-Z0-9\s.,'/\-_@+()%\u00C0-\u017F]*$/;

    if (!safePattern.test(value)) {
      return "Only letters, numbers, spaces, and basic punctuation (. , ' - _ @ + () % /) are allowed";
    }

    // Additional check: Prevent SQL injection patterns even with apostrophes
    // Block dangerous apostrophe patterns like: ' OR '1'='1
    const dangerousApostrophePatterns = [
      /'\s*(OR|AND)\s*'/gi, // ' OR ' or ' AND '
      /'\s*=\s*'/g, // ' = '
      /'\s*(OR|AND)\s*\d+\s*=\s*\d+/gi, // ' OR 1=1
      /';\s*(DROP|DELETE|UPDATE|INSERT)/gi, // '; DROP
      /'\s*--/g, // ' --
      /'\s*\/\*/g, // ' /*
      /\\'/g // Escaped quotes \'
    ];

    for (const pattern of dangerousApostrophePatterns) {
      if (pattern.test(value)) {
        return 'Invalid character pattern detected';
      }
    }

    return true;
  },

  // Safe text with apostrophe (for longer text like descriptions)
  safeText: (value: string): boolean | string => {
    if (!value) return true;

    // Allow apostrophes and more characters for natural text
    // But still block dangerous patterns
    const dangerousPatterns = [
      /<script/gi,
      /javascript:/gi,
      /on\w+\s*=/gi, // Event handlers (onclick, onerror, etc.)
      /'\s*(OR|AND)\s*'/gi, // SQL: ' OR ', ' AND '
      /'\s*(OR|AND)\s*\d+\s*=\s*\d+/gi, // SQL: ' OR 1=1
      // /(SELECT|INSERT|UPDATE|DELETE|DROP|EXEC|UNION)\s+/gi,
      // /;\s*(DROP|DELETE|TRUNCATE|ALTER)/gi,
      /(--|#|\/\*|\*\/)/g, // SQL comments (but allow -- in regular text)
      /\\x[0-9A-F]{2}/gi, // Hex encoding
      /(UNION.*SELECT)/gi,
      /(xp_|sp_)/gi // SQL Server procedures
    ];

    for (const pattern of dangerousPatterns) {
      if (pattern.test(value)) {
        return 'Invalid characters or patterns detected in text';
      }
    }

    // Additional check: Count apostrophes - if too many, might be suspicious
    const apostropheCount = (value.match(/'/g) || []).length;
    if (apostropheCount > value.length / 10) {
      // More than 10% apostrophes is suspicious
      return 'Too many special characters detected';
    }

    return true;
  },

  // Username/identifier validator (no SQL injection possible)
  safeIdentifier: (value: string): boolean | string => {
    if (!value) return true;

    // Only alphanumeric, underscore, hyphen, and dot
    const identifierPattern = /^[a-zA-Z0-9._-]+$/;

    if (!identifierPattern.test(value)) {
      return 'Only letters, numbers, dots, hyphens, and underscores are allowed';
    }

    // Additional check for SQL keywords
    const sqlKeywords = [
      'SELECT',
      'INSERT',
      'DELETE',
      'UPDATE',
      'DROP',
      'EXEC',
      'UNION'
    ];
    const upperValue = value.toUpperCase();

    if (sqlKeywords.some((keyword) => upperValue === keyword)) {
      return 'This value is not allowed';
    }

    return true;
  },

  // Email with SQL injection prevention
  emailSafe: (value: string): boolean | string => {
    if (!value) return true;

    // Check for SQL injection first
    const sqlCheck = validators.noSqlInjection(value);
    if (sqlCheck !== true) return sqlCheck;

    // Then validate email format
    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(value) || 'Please enter a valid email address';
  },

  // URL with SQL injection prevention
  urlSafe: (value: string): boolean | string => {
    if (!value) return true;

    // Check for SQL injection patterns
    const sqlCheck = validators.noSqlInjection(value);
    if (sqlCheck !== true) return sqlCheck;

    // Validate URL
    try {
      const url = new URL(value);
      // Only allow http and https
      if (!['http:', 'https:'].includes(url.protocol)) {
        return 'Only HTTP and HTTPS URLs are allowed';
      }
      return true;
    } catch {
      return 'Please enter a valid URL';
    }
  },

  // Number input (safe by design, but ensure it's actually a number)
  safeNumber: (value: any): boolean | string => {
    if (value === null || value === undefined || value === '') return true;

    // Ensure it's actually a number, not a string with SQL
    if (typeof value === 'string') {
      // Check for non-numeric characters except decimal point
      if (!/^-?\d*\.?\d+$/.test(value)) {
        return 'Please enter a valid number';
      }
    }

    const num = Number(value);
    if (isNaN(num)) {
      return 'Please enter a valid number';
    }

    return true;
  },

  // Search query validator (more lenient but still safe)
  safeSearchQuery: (value: string): boolean | string => {
    if (!value) return true;

    // Block SQL keywords but allow most other characters
    const dangerousSqlPatterns = [
      /(SELECT|INSERT|UPDATE|DELETE|DROP|EXEC|EXECUTE)\s/gi,
      /(--|\/\*|\*\/)/g, // SQL comments
      /(UNION.*SELECT)/gi,
      /(;)/g // Semicolons
    ];

    for (const pattern of dangerousSqlPatterns) {
      if (pattern.test(value)) {
        return 'Your search contains invalid characters';
      }
    }

    return true;
  },

  // Filename validator
  safeFilename: (value: string): boolean | string => {
    if (!value) return true;

    // Only allow safe filename characters
    const filenamePattern = /^[a-zA-Z0-9._-]+$/;

    if (!filenamePattern.test(value)) {
      return 'Filename can only contain letters, numbers, dots, hyphens, and underscores';
    }

    // Block potentially dangerous extensions
    const dangerousExtensions = [
      '.exe',
      '.bat',
      '.cmd',
      '.sh',
      '.php',
      '.asp',
      '.jsp',
      '.sql'
    ];
    const lowerValue = value.toLowerCase();

    if (dangerousExtensions.some((ext) => lowerValue.endsWith(ext))) {
      return 'This file type is not allowed';
    }

    return true;
  },

  // Date input validator (prevent SQL injection in date fields)
  safeDate: (value: string): boolean | string => {
    if (!value) return true;

    // Only allow date format: YYYY-MM-DD or DD/MM/YYYY or MM/DD/YYYY
    const datePattern = /^(\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4})$/;

    if (!datePattern.test(value)) {
      return 'Please enter a valid date format (YYYY-MM-DD or DD/MM/YYYY)';
    }

    // Validate actual date
    if (isNaN(Date.parse(value))) {
      return 'Please enter a valid date';
    }

    return true;
  }
};

/**
 * Predefined validation rule sets for common form field types.
 *
 * @remarks
 * This object provides ready-to-use validation rules that can be applied to form fields.
 * Each rule set is an array of validators that will be executed in order.
 *
 * @example
 * ```typescript
 * // Using a simple rule set
 * const emailValidation = rules.email;
 *
 * // Using a function-based rule set
 * const passwordConfirmation = rules.confirmPassword('myPassword123');
 * ```
 * Available rule sets:
 * - **Email**: `email`, `emailOptional`, `emailArray`, `emailArrayOptional`
 * - **Password**: `password`, `passwordSimple`, `confirmPassword()`
 * - **Personal Info**: `name`, `username`, `phone`, `phoneOptional`, `age`, `dateOfBirth`
 * - **Web**: `website`, `websiteOptional`, `websiteWithParams`, `websiteWithParamsOptional`, `url`
 * - **Address**: `address`, `city`, `postalCode`
 * - **Text**: `required`, `requiredText`, `description`, `descriptionOptional`, `freeText`, `freeTextOptional`
 * - **Business**: `companyName`
 * - **Payment**: `creditCard`
 * - **Date**: `date`, `dateOfBirth`
 */
const rules = {
  // Email rules
  email: [validators.required, validators.email],

  // Email (optional)
  emailOptional: [validators.email],

  // Email array
  emailArray: [validators.emailArray],

  // Email array (optional)
  emailArrayOptional: [validators.emailArrayOptional],

  // Password rules
  password: [
    validators.required,
    validators.minLength(8),
    validators.strongPassword
  ],

  // Password (simple - just required and min length)
  passwordSimple: [validators.required, validators.minLength(8)],

  // Confirm password (use as function)
  confirmPassword: (password: string) => [
    validators.required,
    validators.match(password, 'password')
  ],

  // Name rules
  name: [
    validators.required,
    validators.minLength(2),
    validators.maxLength(50),
    validators.alpha
  ],

  // Name rules (optional)
  nameOptional: [
    validators.minLength(2),
    validators.maxLength(50),
    validators.alpha
  ],

  // Username rules
  username: [
    validators.required,
    validators.minLength(3),
    validators.maxLength(20),
    validators.alphanumeric
  ],

  // Phone rules
  phone: [validators.required, validators.phone],

  // Phone (optional)
  phoneOptional: [validators.phone],

  // Age rules
  age: [
    validators.required,
    validators.numeric,
    validators.min(18),
    validators.max(120)
  ],

  // Required field (generic)
  required: [validators.required],

  // Required text
  requiredText: [
    validators.required,
    validators.minLength(1),
    validators.maxLength(255)
  ],

  // Website URL
  website: [validators.required, validators.url],

  // Website URL (optional)
  websiteOptional: [validators.url],

  // Website with query parameters
  websiteWithParams: [
    validators.required,
    validators.pattern(
      /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)(\?[/\w .&=-]*)?$/i,
      'Please enter a valid URL'
    )
  ],

  // Website with query parameters (optional)
  websiteWithParamsOptional: [
    validators.pattern(
      /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)(\?[/\w .&=-]*)?$/i,
      'Please enter a valid URL with optional query parameters'
    )
  ],

  // Credit card
  creditCard: [validators.required, validators.creditCard],

  // Date of birth
  dateOfBirth: [validators.required, validators.date, validators.minAge(18)],

  // Date (generic)
  date: [validators.required, validators.date],

  // Postal/ZIP code
  postalCode: [
    validators.required,
    validators.minLength(5),
    validators.maxLength(10),
    validators.alphanumeric
  ],

  // Address
  address: [
    validators.required,
    validators.minLength(5),
    validators.maxLength(200)
  ],

  // City
  city: [
    validators.required,
    validators.minLength(2),
    validators.maxLength(50),
    validators.alpha
  ],

  // Company name
  companyName: [
    validators.required,
    validators.minLength(2),
    validators.maxLength(100)
  ],

  // Description/Textarea
  description: [
    validators.required,
    validators.minLength(10),
    validators.maxLength(1000)
  ],

  // Description (optional)
  descriptionOptional: [validators.maxLength(1000)],

  // Free text
  freeText: [
    validators.required,
    validators.pattern(patternInput, errorMessage)
  ],

  // Free text (optional)
  freeTextOptional: [validators.pattern(patternInput, errorMessage)],

  /**
   * SQL Injection Safe Rules
   */

  // Safe general input
  safeInput: [validators.required, validators.safeInput],

  // Safe input (optional)
  safeInputOptional: [validators.safeInput],

  // Safe text area
  safeText: [
    validators.required,
    validators.safeText,
    validators.maxLength(5000)
  ],

  // Safe text (optional)
  safeTextOptional: [validators.safeText, validators.maxLength(5000)],

  // Safe username/identifier
  safeUsername: [
    validators.required,
    validators.minLength(3),
    validators.maxLength(50),
    validators.safeIdentifier
  ],

  // Safe email
  emailSafe: [validators.required, validators.emailSafe],

  // Safe URL
  urlSafe: [validators.urlSafe],

  // Safe search
  safeSearch: [validators.safeSearchQuery, validators.maxLength(500)],

  // Safe number
  safeNumber: [validators.required, validators.safeNumber],

  // Safe filename
  safeFilename: [validators.required, validators.safeFilename],

  // Safe date
  safeDate: [validators.required, validators.safeDate],

  // No SQL injection check (for any field)
  noSqlInjection: [validators.noSqlInjection],

  // Combine safe input with no SQL injection
  ultraSafe: [
    validators.required,
    validators.safeInput,
    validators.noSqlInjection
  ]
};

export default rules;

export {
  rules,
  validators,
  patternInput as patternInputSpecialCharacter,
  errorMessage as errorMessageSpecialCharacter
};
